import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useShop } from "../contexts/ShopContext";
import { useI18n } from "../contexts/I18nContext";
import { SearchSuggestionsPanel, useSearchSuggestions } from "./SearchSuggestions";
import { isAdminRole } from "../utils/roles";

const SearchIcon = ({ size = 24 }) => (
  <svg width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="11" cy="11" r="6.75" />
    <path strokeLinecap="round" d="m16 16 4 4" />
  </svg>
);

const MenuIcon = ({ open }) => (
  <svg width="25" height="25" fill="none" stroke="currentColor" strokeWidth="1.7" viewBox="0 0 24 24" aria-hidden="true">
    {open ? <path strokeLinecap="round" d="M5 5l14 14M19 5 5 19" /> : <path strokeLinecap="round" d="M3 6.5h18M3 12h18M3 17.5h18" />}
  </svg>
);

const BagIcon = () => (
  <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M7 8.5h10l1 12H6l1-12Z" />
    <path strokeLinecap="round" d="M9 9V6.5a3 3 0 0 1 6 0V9" />
  </svg>
);

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchActiveIndex, setSearchActiveIndex] = useState(-1);
  const [langOpen, setLangOpen] = useState(false);
  const searchRef = useRef(null);
  const mobileSearchRef = useRef(null);
  const langRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { cartCount } = useShop();
  const { lang, setLang, t, LANG_OPTIONS } = useI18n();
  const isAdmin = isAdminRole(user?.role);
  const suggestions = useSearchSuggestions(searchQuery, searchOpen, { includeRental: true, limit: 7 });

  const navLinks = [
    { to: "/store", label: t.store || "스토어", sub: "복합기 · 프린터 · 토너" },
    { to: "/rental", label: t.rental || "렌탈", sub: "전국 설치 · 유지보수" },
    { to: "/consultation", label: t.inquiry || "상담", sub: "견적 · A/S · 도입 문의" },
    { to: "/compare", label: t.compare || "비교", sub: "제품 사양 한눈에" },
    { to: "/community", label: t.reviewLounge || "커뮤니티", sub: "설치 사례 · 사용자 후기" },
    { to: "/b2b", label: "B2B", sub: "사업자 전용 혜택" },
  ];

  useEffect(() => {
    setMenuOpen(false);
    setLangOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const closeFloating = (event) => {
      const inSearch = searchRef.current?.contains(event.target) || mobileSearchRef.current?.contains(event.target);
      if (!inSearch) setSearchOpen(false);
      if (!langRef.current?.contains(event.target)) setLangOpen(false);
    };
    document.addEventListener("mousedown", closeFloating);
    return () => document.removeEventListener("mousedown", closeFloating);
  }, []);

  useEffect(() => setSearchActiveIndex(-1), [searchQuery]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onEscape = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        setSearchOpen(false);
      }
    };
    document.addEventListener("keydown", onEscape);
    return () => document.removeEventListener("keydown", onEscape);
  }, [menuOpen]);

  const submitSearch = (value = searchQuery) => {
    const query = value.trim();
    if (!query) return;
    navigate(`/store?q=${encodeURIComponent(query)}`);
    setSearchQuery("");
    setSearchOpen(false);
    setMenuOpen(false);
  };

  const pickProduct = (product) => {
    if (!product?.slug) return;
    navigate(`/p/${product.slug}`);
    setSearchQuery("");
    setSearchOpen(false);
    setMenuOpen(false);
  };

  const handleSearchKeyDown = (event) => {
    if (event.nativeEvent.isComposing || event.keyCode === 229) return;
    const products = suggestions.products || [];
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setSearchOpen(true);
      setSearchActiveIndex((index) => (products.length ? (index + 1) % products.length : -1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setSearchActiveIndex((index) => (products.length ? (index <= 0 ? products.length - 1 : index - 1) : -1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (searchActiveIndex >= 0 && products[searchActiveIndex]) pickProduct(products[searchActiveIndex]);
      else submitSearch();
    } else if (event.key === "Escape") {
      setSearchOpen(false);
    }
  };

  // 컴포넌트가 아닌 렌더 함수: 컴포넌트로 두면 Navbar 리렌더마다 리마운트되어
  // 타이핑 한 글자마다 입력 포커스가 풀린다(모바일 키보드 닫힘).
  const renderSearchForm = (mobile = false) => (
    <form
      className={mobile ? "dept-nav-mobile-search" : "dept-nav-search"}
      ref={mobile ? mobileSearchRef : searchRef}
      onSubmit={(event) => {
        event.preventDefault();
        if (!searchQuery.trim()) {
          event.currentTarget.querySelector("input")?.focus();
          return;
        }
        submitSearch();
      }}
    >
      <input
        value={searchQuery}
        onChange={(event) => setSearchQuery(event.target.value)}
        onFocus={() => setSearchOpen(true)}
        onKeyDown={handleSearchKeyDown}
        placeholder="모델명 · 토너명 검색"
        aria-label="제품 검색"
        aria-autocomplete="list"
      />
      <button type="submit" aria-label="검색"><SearchIcon size={mobile ? 25 : 22} /></button>
      {searchOpen && (
        <SearchSuggestionsPanel
          query={searchQuery}
          suggestions={suggestions}
          activeIndex={searchActiveIndex}
          compact={!mobile}
          onPickProduct={pickProduct}
          onPickKeyword={(keyword) => {
            setSearchQuery(keyword);
            setSearchOpen(true);
          }}
          onSubmit={() => submitSearch()}
          style={{ width: mobile ? "100%" : "min(620px, calc(100vw - 40px))" }}
        />
      )}
    </form>
  );

  return (
    <header className={`dept-header${menuOpen ? " is-menu-open" : ""}`}>
      <div className="dept-service-bar">
        <div className="dept-service-bar__inner">
          <span>복합기 · 프린터 · 토너 전문 공급</span>
          <div><span>평일 09:00 - 18:00</span><a href="tel:1600-7542">1600-7542</a></div>
        </div>
      </div>

      <div className="dept-main-nav">
        <div className="dept-main-nav__inner">
          <button type="button" className="dept-menu-button" onClick={() => setMenuOpen((open) => !open)} aria-expanded={menuOpen} aria-controls="department-menu" aria-label={menuOpen ? "메뉴 닫기" : "전체 메뉴"}>
            <MenuIcon open={menuOpen} /><span>{menuOpen ? "닫기" : "전체 메뉴"}</span>
          </button>

          <Link to="/" className="dept-wordmark" aria-label="일광오피스 홈">
            <strong>일광오피스</strong><span>ILKWANG OFFICE</span>
          </Link>

          <div className="dept-nav-actions">
            {renderSearchForm()}
            <div className="dept-language" ref={langRef}>
              <button type="button" onClick={() => setLangOpen((open) => !open)} aria-expanded={langOpen}>{String(lang || "ko").toUpperCase()}</button>
              {langOpen && (
                <div className="dept-language__menu">
                  {(LANG_OPTIONS || []).map((option) => (
                    <button key={option.code} type="button" className={lang === option.code ? "is-active" : ""} onClick={() => { setLang(option.code); setLangOpen(false); }}>
                      <span>{option.label}</span><small>{option.code.toUpperCase()}</small>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Link className="dept-cart-link" to="/cart" aria-label={`${t.cart || "장바구니"} ${cartCount || 0}개`}>
              <BagIcon />{cartCount > 0 && <span>{cartCount > 99 ? "99+" : cartCount}</span>}
            </Link>
            <Link className="dept-account-link" to={user ? (isAdmin ? "/admin" : "/my") : "/login"} state={user ? undefined : { from: location.pathname }}>
              {user ? (isAdmin ? "관리" : "마이") : t.login || "로그인"}
            </Link>
          </div>

          <div className="ik-mobile-actions">
            <button type="button" className="ik-search-toggle" aria-label="제품 검색 열기" onClick={() => {
              setMenuOpen(true);
              requestAnimationFrame(() => mobileSearchRef.current?.querySelector("input")?.focus({ preventScroll: true }));
            }}><SearchIcon size={22} /></button>
            <Link className="dept-mobile-cart" to="/cart" aria-label={`${t.cart || "장바구니"} ${cartCount || 0}개`}>
              <BagIcon />{cartCount > 0 && <span>{cartCount > 99 ? "99+" : cartCount}</span>}
            </Link>
          </div>
        </div>
      </div>

      <nav className="dept-quick-nav" aria-label="주요 메뉴">
        <div>
          {navLinks.map((item) => <Link key={item.to} to={item.to} className={location.pathname.startsWith(item.to) ? "is-active" : ""}>{item.label}</Link>)}
        </div>
      </nav>

      {menuOpen && (
        <div id="department-menu" className="dept-mega-menu">
          <div className="dept-mega-menu__inner">
            <div className="dept-mega-menu__search">{renderSearchForm(true)}</div>
            <div className="dept-mega-menu__links">
              {navLinks.map((item, index) => (
                <Link key={item.to} to={item.to}>
                  <span className="dept-mega-menu__number">0{index + 1}</span><strong>{item.label}</strong><small>{item.sub}</small>
                </Link>
              ))}
            </div>
            <div className="dept-mega-menu__bottom">
              <div className="dept-mega-menu__languages">
                {(LANG_OPTIONS || []).map((option) => <button key={option.code} type="button" onClick={() => setLang(option.code)} className={lang === option.code ? "is-active" : ""}>{option.code.toUpperCase()}</button>)}
              </div>
              <div className="dept-mega-menu__account">
                {user ? <><Link to={isAdmin ? "/admin" : "/my"}>{isAdmin ? "관리자" : "마이페이지"}</Link><button type="button" onClick={logout}>로그아웃</button></> : <Link to="/login" state={{ from: location.pathname }}>로그인</Link>}
                <a href="tel:1600-7542">전화 상담 1600-7542</a>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
